import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import Swal from 'sweetalert2';

interface SaasModule {
  id: string; name: string; description: string | null; category: string;
  is_core: boolean; is_addon: boolean; default_enabled: boolean; price_monthly: number; is_active: boolean;
}

interface Props {
  module: SaasModule | null;
  onClose: () => void;
  onSaved: () => void;
}

const CATEGORIES = ['Production', 'Website', 'Automation', 'Settings'];
const SW = { background: '#ffffff', color: '#0f172a' };

const EMPTY: SaasModule = {
  id: '', name: '', description: '', category: 'Production',
  is_core: false, is_addon: true, default_enabled: false, price_monthly: 0, is_active: true,
};

export default function ModuleModal({ module, onClose, onSaved }: Props) {
  const isEdit = !!module;
  const [form, setForm]     = useState<SaasModule>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (module) setForm({ ...module, description: module.description ?? '' });
    else setForm(EMPTY);
  }, [module]);

  const set = (key: keyof SaasModule, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.id.trim() || !form.name.trim()) {
      await Swal.fire({ icon: 'warning', title: 'Campos requeridos', text: 'ID y nombre son obligatorios.', ...SW });
      return;
    }
    setSaving(true);
    const payload = {
      id: form.id.trim().toLowerCase().replace(/\s+/g, '_'),
      name: form.name.trim(), description: form.description?.trim() || null,
      category: form.category, is_core: form.is_core, is_addon: form.is_addon,
      default_enabled: form.default_enabled, price_monthly: Number(form.price_monthly), is_active: form.is_active,
    };
    const { error } = isEdit
      ? await supabase.from('saas_modules').update(payload).eq('id', module!.id)
      : await supabase.from('saas_modules').insert(payload);
    setSaving(false);
    if (error) { await Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    onSaved(); onClose();
  };

  const Toggle = ({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) => (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-slate-600 text-sm">{label}</span>
      <button type="button" onClick={() => onChange(!value)}
        className={`relative w-10 h-5 rounded-full transition-colors ${value ? 'bg-blue-600' : 'bg-slate-200'}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${value ? 'left-5' : 'left-0.5'}`} />
      </button>
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div>
            <h2 className="text-slate-900 font-semibold text-base">{isEdit ? 'Editar Módulo' : 'Nuevo Módulo'}</h2>
            <p className="text-slate-500 text-xs mt-0.5">{isEdit ? `ID: ${module!.id}` : 'Agrega un módulo al catálogo global'}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 cursor-pointer"><i className="ri-close-line text-lg"></i></button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto max-h-[70vh]">
          {!isEdit && (
            <div>
              <label className="block text-slate-500 text-xs mb-1.5 uppercase tracking-wide">ID del módulo</label>
              <input value={form.id} onChange={(e) => set('id', e.target.value.toLowerCase().replace(/\s+/g, '_'))} placeholder="ej. photo_selection"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-blue-400" />
              <p className="text-slate-400 text-[11px] mt-1">Snake_case, único. Ej: ai_sales_assistant</p>
            </div>
          )}

          <div>
            <label className="block text-slate-500 text-xs mb-1.5 uppercase tracking-wide">Nombre</label>
            <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="ej. Photo Selection"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-blue-400" />
          </div>

          <div>
            <label className="block text-slate-500 text-xs mb-1.5 uppercase tracking-wide">Descripción</label>
            <textarea value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} rows={2} maxLength={300}
              placeholder="Describe brevemente qué hace este módulo..."
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-blue-400 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-500 text-xs mb-1.5 uppercase tracking-wide">Categoría</label>
              <select value={form.category} onChange={(e) => set('category', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:border-blue-400">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-slate-500 text-xs mb-1.5 uppercase tracking-wide">Precio mensual (USD)</label>
              <input type="number" min={0} step={1} value={form.price_monthly} onChange={(e) => set('price_monthly', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:border-blue-400" />
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
            <Toggle label="Módulo Core (incluido en todos los planes)" value={form.is_core} onChange={(v) => set('is_core', v)} />
            <Toggle label="Add-on (se contrata aparte)" value={form.is_addon} onChange={(v) => set('is_addon', v)} />
            <Toggle label="Habilitado por defecto en nuevos tenants" value={form.default_enabled} onChange={(v) => set('default_enabled', v)} />
            <Toggle label="Activo globalmente" value={form.is_active} onChange={(v) => set('is_active', v)} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap">
            {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear módulo'}
          </button>
        </div>
      </div>
    </div>
  );
}
